/**
 * 生命靈數計算器 — 移植自 LINE bot
 * 純數學計算，無外部依賴
 */

const MASTER_NUMBERS = new Set([11, 22, 33]);

export const NUMBER_MEANINGS = {
  1: { title: '開創者', keywords: ['開路精神', '自主意識'], strengths: '擅長發起行動、訂下方向並驅動前進。', watchouts: '留心過度主觀或拒絕協調。', missingAdvice: '練習主動表達與做決定，有助建立自我驅動力。' },
  2: { title: '協調者', keywords: ['同理', '傾聽'], strengths: '善於營造和諧氛圍、調整彼此節奏。', watchouts: '避免過度迎合或陷入自我懷疑。', missingAdvice: '多建立合作機會與信任練習，補足柔性的互動力。' },
  3: { title: '表達者', keywords: ['靈感', '魅力'], strengths: '溝通與呈現能力佳，能帶來愉悅氛圍。', watchouts: '注意落實計畫，避免流於只談不做。', missingAdvice: '透過寫作、分享或表演等方式激活創意與自信。' },
  4: { title: '建築家', keywords: ['秩序', '穩固'], strengths: '擅長規劃、建立流程並確保執行細節。', watchouts: '避免過度保守或僵化。', missingAdvice: '養成紀律與規劃能力，增添腳踏實地的力量。' },
  5: { title: '探索者', keywords: ['自由', '冒險'], strengths: '喜歡嘗試新事物、勇於改變並帶來活力。', watchouts: '注意分心與不安定。', missingAdvice: '安排旅行、學習新技能，讓變化感成為養分。' },
  6: { title: '關懷者', keywords: ['責任', '關懷'], strengths: '重視家庭與團隊，樂於支持身邊的人。', watchouts: '避免過度操心或犧牲自己。', missingAdvice: '練習承擔與照顧，培養穩定而溫暖的影響力。' },
  7: { title: '思想家', keywords: ['分析', '思辨'], strengths: '善於研究、深入問題核心並提煉觀點。', watchouts: '留心與人疏離或鑽牛角尖。', missingAdvice: '透過冥想、閱讀或研究訓練思考力，補足內省面向。' },
  8: { title: '成就者', keywords: ['策略', '權能'], strengths: '擅長整合資源、規劃成果並付諸行動。', watchouts: '避免焦躁或過度控制。', missingAdvice: '練習財務管理與決策力，建立實際的影響範圍。' },
  9: { title: '智者', keywords: ['願景', '分享'], strengths: '具理想與同理心，樂於傳遞溫暖與智慧。', watchouts: '注意過度浪漫化或忽略自身需求。', missingAdvice: '參與公益或支持型活動，養成廣闊的人文視野。' },
};

const LINE_PATTERNS = [
  { digits: [1, 2, 3], label: '行動列', description: '構想、協調與展現兼具，推動事情具節奏感。' },
  { digits: [4, 5, 6], label: '實務列', description: '有能力規劃、調整與落實，適合處理流程或系統。' },
  { digits: [7, 8, 9], label: '願景列', description: '兼具洞察、資源與大局觀，容易擴展長遠目標。' },
  { digits: [1, 4, 7], label: '堅毅柱', description: '具備持續力與抗壓性，能穩住團隊節奏。' },
  { digits: [2, 5, 8], label: '協作柱', description: '擅長溝通協調與資源連結，適合橋樑角色。' },
  { digits: [3, 6, 9], label: '活力柱', description: '創意、陪伴與感性兼具，氛圍經常充滿熱情。' },
  { digits: [1, 5, 9], label: '靈感斜線', description: '直覺與創意行動相伴，適合領導創新。' },
  { digits: [3, 5, 7], label: '洞察斜線', description: '善於透過研究、表達與觀察綜合判斷。' },
];

function reduceNumber(digits) {
  if (digits.length === 0) return { value: 0, steps: [], isMaster: false, masterNumber: null };

  const steps = [];
  let current = digits.reduce((sum, d) => sum + d, 0);
  steps.push(`${digits.join(' + ')} = ${current}`);

  let masterNumber = null;
  while (current > 9) {
    if (MASTER_NUMBERS.has(current) && masterNumber === null) {
      masterNumber = current;
    }
    const cur = String(current).split('').map(Number);
    const next = cur.reduce((sum, d) => sum + d, 0);
    steps.push(`${cur.join(' + ')} = ${next}`);
    current = next;
  }

  return { value: current, steps, isMaster: masterNumber !== null, masterNumber };
}

/**
 * 計算生命靈數
 * @param {number} year
 * @param {number} month (1-12)
 * @param {number} day (1-31)
 */
export function calculateNumerology(year, month, day) {
  const yearStr = String(year);
  const monthStr = String(month).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');

  const digitCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 };
  const baseDigits = [];

  const recordDigit = (d) => {
    if (d >= 1 && d <= 9) {
      baseDigits.push(d);
      digitCounts[d]++;
    }
  };

  const recordDerived = (number) => {
    if (!number) return;
    String(number).split('').map(Number).filter((d) => d >= 1 && d <= 9).forEach((d) => { digitCounts[d]++; });
  };

  // 收集原始數字
  [...yearStr].forEach((c) => recordDigit(Number(c)));
  [...monthStr].forEach((c) => recordDigit(Number(c)));
  [...dayStr].forEach((c) => recordDigit(Number(c)));

  // 計算核心數字
  const lifePath = reduceNumber([...baseDigits]);
  const birthdayNumber = reduceNumber([...dayStr].map(Number).filter((d) => d >= 0));
  const monthNumber = reduceNumber([...monthStr].map(Number).filter((d) => d >= 0));
  const yearNumber = reduceNumber([...yearStr].map(Number).filter((d) => d >= 0));

  // 把衍生數字也加入統計
  recordDerived(lifePath.value);
  if (lifePath.masterNumber) recordDerived(lifePath.masterNumber);
  recordDerived(birthdayNumber.value);
  recordDerived(monthNumber.value);
  recordDerived(yearNumber.value);

  const missingNumbers = Object.keys(digitCounts).map(Number).filter((d) => digitCounts[d] === 0);
  const dominantNumbers = Object.keys(digitCounts).map(Number).filter((d) => digitCounts[d] >= 2);

  // 連線
  const lines = LINE_PATTERNS
    .filter((p) => p.digits.every((d) => digitCounts[d] > 0))
    .map((p) => ({ combination: p.digits.join('-'), label: p.label, description: p.description }));

  // 缺數建議
  const missingInsights = missingNumbers.map((d) => {
    const m = NUMBER_MEANINGS[d];
    return { digit: d, title: m?.title || `數字 ${d}`, suggestion: m?.missingAdvice || '' };
  });

  const decorateCore = (label, detail) => {
    const m = NUMBER_MEANINGS[detail.value] || {};
    return {
      label, value: detail.value, isMaster: detail.isMaster, masterNumber: detail.masterNumber,
      steps: detail.steps, keywords: m.keywords || [], strengths: m.strengths || '',
      watchouts: m.watchouts || '', summary: m.title || '',
    };
  };

  return {
    input: { year, month, day },
    digits: { baseDigits, counts: digitCounts, dominantNumbers, missingNumbers },
    coreNumbers: {
      lifePath: decorateCore('命數', lifePath),
      birthday: decorateCore('生日體質', birthdayNumber),
      month: decorateCore('月份能量', monthNumber),
      year: decorateCore('年份能量', yearNumber),
    },
    missingInsights,
    lines,
  };
}

export default calculateNumerology;
